import { QRCodeSVG } from 'qrcode.react';

export const MM_TO_PX = 3.78; // ~96dpi, pour l'affichage écran (l'export utilise width_mm/height_mm)

/**
 * Rendu du contenu d'UN élément de carte. Seule source de vérité pour l'éditeur,
 * la prévisualisation, la fiche fidèle et l'export — évite les 4-5 implémentations
 * divergentes qui existaient avant (tailles de photo différentes, QR incohérent...).
 *
 * `el` : élément (CardElement sérialisé). `resolvedValue` : valeur déjà résolue
 * côté backend pour 'field'/'qrcode'/'photo' (undefined = mode édition sans
 * données réelles, affiche un placeholder de type "{{Nom du champ}}").
 */
export function CardElementContent({ el, resolvedValue, resolvedColor, fieldLabel, cardWidthPx }) {
  const style = el.style || {};
  const justify = style.align === 'center' ? 'center' : style.align === 'right' ? 'flex-end' : 'flex-start';
  // colorSource:'status' => la couleur vient du statut du fidèle (réglages église),
  // résolue côté serveur dans resolvedColor ; sans données réelles (mode édition),
  // on retombe sur style.color pour garder un aperçu cohérent.
  const dynamicColor = style.colorSource === 'status' ? (resolvedColor?.from || style.color) : style.color;

  const fontFamily = style.fontFamily || 'inherit';

  const opacity = style.opacity ?? 1;

  if (el.element_type === 'text') {
    return (
      <div style={{ width: '100%', height: '100%', fontSize: style.fontSize || 14, fontFamily, color: dynamicColor || '#1e293b', fontWeight: style.fontWeight || 'normal', fontStyle: style.italic ? 'italic' : 'normal', textAlign: style.align || 'left', lineHeight: style.lineHeight || 1.3, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: justify, opacity }}>
        {el.content}
      </div>
    );
  }

  if (el.element_type === 'field') {
    const label = fieldLabel || el.field_key || 'Champ';
    const prefix = style.showLabel ? `${label} : ` : '';
    return (
      <div style={{ width: '100%', height: '100%', fontSize: style.fontSize || 14, fontFamily, color: dynamicColor || '#1e293b', fontWeight: style.fontWeight || 'normal', fontStyle: style.italic ? 'italic' : 'normal', textAlign: style.align || 'left', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: justify, opacity }}>
        {resolvedValue !== undefined ? (resolvedValue ? `${prefix}${resolvedValue}` : <span style={{ opacity: .4 }}>{label}</span>) : `{{${label}}}`}
      </div>
    );
  }

  if (el.element_type === 'photo') {
    const isCircle = style.borderRadius === 'circle';
    return (
      <div style={{ width: '100%', height: '100%', borderRadius: isCircle ? '50%' : '6px', overflow: 'hidden', border: `${style.borderWidth || 0}px solid ${style.borderColor || 'transparent'}`, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 11 }}>
        {resolvedValue ? <img src={resolvedValue} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : 'Photo'}
      </div>
    );
  }

  if (el.element_type === 'image') {
    return (
      <div style={{ width: '100%', height: '100%', opacity: style.opacity ?? 1, overflow: 'hidden', borderRadius: style.borderRadius || 0, background: el.image_url ? 'transparent' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 11 }}>
        {el.image_url ? <img src={el.image_url} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: style.fit || 'cover' }} /> : 'Image'}
      </div>
    );
  }

  if (el.element_type === 'qrcode') {
    const value = resolvedValue || 'APERCU';
    const widthPx = cardWidthPx ? (el.width / 100) * cardWidthPx : el.width * MM_TO_PX;
    const heightPx = cardWidthPx ? (el.height / 100) * (cardWidthPx * 0.63) : el.height * MM_TO_PX;
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
        <QRCodeSVG value={String(value)} size={Math.max(20, Math.min(widthPx, heightPx) * 0.9)} fgColor={style.fgColor || '#000000'} level="M" />
      </div>
    );
  }

  if (el.element_type === 'shape') {
    const isCircle = style.shape === 'circle';
    const isLine = style.shape === 'line';
    const shapeColor = style.colorSource === 'status' ? (resolvedColor?.from || style.background) : style.background;
    return (
      <div style={{
        width: '100%', height: isLine ? (style.borderWidth || 2) : '100%',
        borderRadius: isCircle ? '50%' : (style.borderRadius || 0),
        background: isLine ? (style.borderColor || '#94a3b8') : (shapeColor || '#e2e8f0'),
        border: isLine ? 'none' : `${style.borderWidth || 0}px solid ${style.borderColor || 'transparent'}`,
        opacity: style.opacity ?? 1,
      }} />
    );
  }

  return null;
}

/**
 * Rendu complet d'un côté de carte (recto ou verso) : fond + tous ses éléments
 * positionnés en absolu selon leurs %. `side` : objet {background_color,
 * background_image_url, elements: [{...el, resolved_value}]}. `fieldChoices` :
 * liste [{key,label}] pour afficher un libellé lisible en mode édition sans données.
 */
export default function CardRenderer({ side, widthPx, heightPx, fieldChoices = [], style = {} }) {
  if (!side) {
    return (
      <div style={{ width: widthPx, height: heightPx, background: '#f1f5f9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, ...style }}>
        Aucun template
      </div>
    );
  }
  return (
    <div style={{
      position: 'relative', width: widthPx, height: heightPx, overflow: 'hidden',
      background: side.background_image_url ? `url(${side.background_image_url}) center/cover` : (side.background_color || '#ffffff'),
      ...style,
    }}>
      {[...side.elements].filter(e => e.visible !== false).sort((a, b) => a.z_index - b.z_index).map(el => (
        <div key={el.id} style={{
          position: 'absolute',
          left: `${el.x}%`, top: `${el.y}%`, width: `${el.width}%`, height: `${el.height}%`,
          transform: `rotate(${el.rotation || 0}deg)`,
        }}>
          <CardElementContent
            el={el}
            resolvedValue={el.resolved_value}
            resolvedColor={el.resolved_color}
            fieldLabel={fieldChoices.find(f => f.key === el.field_key)?.label}
            cardWidthPx={widthPx}
          />
        </div>
      ))}
    </div>
  );
}
